import React, { Component } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import withFontClassName from '../../hoc/with-font-class-name';
import scrollParent from '../../services/scroll-parent';
import Button, { type ButtonProps } from '../button';
import MoreIcon from '../icons/more-icon';
import styles from './dropdown-button.scss';

const POSITION_RIGHT = 'right';
const POSITION_CENTER = 'center';

const ICON_RIGHT_OFFSET = 22;

type Position = typeof POSITION_RIGHT | typeof POSITION_CENTER;

type Props = {
  /** Resolves the menu contents lazily, on first open. */
  children: () => React.ReactNode | Promise<React.ReactNode>;
  icon?: React.ReactNode;
  buttonProps?: Partial<ButtonProps>;
  position?: Position;
  top?: boolean;
  isDisabled?: boolean;
  actionsContainerClassName?: string;
  contentFontClassName?: string;
};

type State = {
  position: Position;
  isMenuVisible: boolean;
  top: boolean;
  positionOffset?: number;
};

class DropdownButton extends Component<Props, State> {
  static POSITION_RIGHT = POSITION_RIGHT;

  actionsContainer: HTMLDivElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      position: props.position || POSITION_CENTER,
      isMenuVisible: false,
      top: false,
    };
  }

  component: React.ReactNode = null;

  rootRef: HTMLSpanElement | null = null;

  componentWillUnmount() {
    document.removeEventListener('click', this.hideComponent);
  }

  reposition = () => {
    if (!this.actionsContainer || !this.rootRef) {
      return;
    }

    const { position } = this.state;
    const trigger = this.rootRef.getBoundingClientRect();
    const actionsRect = this.actionsContainer.getBoundingClientRect();
    const scrollParentRect = scrollParent()!.getBoundingClientRect()!;
    const positionOffset = ICON_RIGHT_OFFSET - Math.ceil(trigger.width / 2);

    if (scrollParentRect.bottom - actionsRect.bottom < 0 || this.props.top) {
      this.setState({
        top: true,
      });
    }

    if (position === POSITION_CENTER && actionsRect.right > window.innerWidth) {
      this.setState({
        position: POSITION_RIGHT,
        positionOffset,
      });
    }

    if (position === POSITION_RIGHT) {
      this.setState({
        positionOffset,
      });
    }
  };

  handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.props.isDisabled) {
      return;
    }

    if (this.state.isMenuVisible) {
      this.hideComponent();
    } else {
      this.showComponent();
    }
  };

  async showComponent() {
    this.component = await this.props.children();

    document.addEventListener('click', this.hideComponent);
    this.setState(
      {
        isMenuVisible: true,
      },
      this.reposition,
    );
  }

  hideComponent = () => {
    document.removeEventListener('click', this.hideComponent);
    this.setState({
      isMenuVisible: false,
    });
  };

  renderMenu() {
    const { contentFontClassName, actionsContainerClassName } = this.props;
    const { isMenuVisible, position, positionOffset, top } = this.state;

    const actionsClassName = classNames(
      actionsContainerClassName,
      styles.actionsContainer,
      contentFontClassName,
      'blog-text-color',
      'blog-card-border-color',
      {
        [styles.top]: top,
      },
    );
    const triangleClassName = classNames(
      styles.triangle,
      'blog-card-border-color',
      'blog-card-background-color',
    );

    const actionsStyle: React.CSSProperties = {};
    if (position === POSITION_RIGHT) {
      actionsStyle.transform = `translateX(${positionOffset}px)`;
    }

    if (isMenuVisible) {
      return (
        <div
          className={actionsClassName}
          style={actionsStyle}
          ref={(ref) => (this.actionsContainer = ref)}
        >
          <div className={classNames(triangleClassName, 'blog-card-background-color')} />
          <div className="blog-card-background-color">{this.component}</div>
        </div>
      );
    }
  }

  render() {
    const { icon, buttonProps = {} } = this.props;
    const { position } = this.state;
    const useFixedColor = get(buttonProps, 'useFixedColor');

    const moreButton = (
      <Button className={classNames(styles.button, 'profile-actions-button')} {...buttonProps}>
        <MoreIcon className={useFixedColor ? styles.fixedColor : 'blog-button-primary-icon-fill'} />
      </Button>
    );
    return (
      <span
        ref={(ref) => (this.rootRef = ref)}
        data-hook="dropdown-button"
        className={classNames(styles.container, styles[position])}
        onClick={this.handleClick}
      >
        {icon ? icon : moreButton}
        {this.renderMenu()}
      </span>
    );
  }
}

export default withFontClassName(DropdownButton);
